import * as React from 'react'
import {
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
} from 'cs-kevlar-react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'

export enum ConfigurationGroup {
  Root,
  Devices,
  Notifications,
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: {
      marginTop: 16,
      marginLeft: 16,
      width: 400,
    },
    sectionHeader: {
      backgroundColor: theme.palette.neutral.light,
    },
    sectionContent: {
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      padding: 0,
    },
    sectionTitle: {
      marginTop: 36,
      marginLeft: 16,
      marginBottom: 8,
      fontSize: 24,
    },
    sectionDescription: {
      marginLeft: 16,
      marginBottom: 36,
      fontSize: 14,
      opacity: 0.54,
    },
    sectionSettings: {
      marginLeft: 0,
      marginBottom: 0,
      marginTop: 0,
    },
    sectionAction: {
      marginTop: 0,
      marginLeft: 0,
      marginBottom: 0,
    },
  }),
)

interface ConfigurationCardProps {
  title: string
  description: string
  settings: ConfigurationSetting[]
  onEdit: (type: ConfigurationGroup) => void
  editText?: string
  group: ConfigurationGroup
  children?: (className: string) => JSX.Element
}

interface ConfigurationSetting {
  name: string
  value: string
}

export const ConfigurationCard: React.FunctionComponent<ConfigurationCardProps> = ({
  title,
  description,
  settings,
  onEdit,
  editText = 'Edit',
  group,
  children,
}) => {
  const classes = useStyles({})

  const onEditClicked = React.useMemo(() => {
    return (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      return onEdit(group)
    }
  }, [onEdit, group])

  return (
    <Card className={classes.section}>
      <CardContent className={classes.sectionContent}>
        <Grid container direction="column">
          <Grid className={classes.sectionHeader}>
            <Grid item>
              <Typography className={classes.sectionTitle}>{title}</Typography>
            </Grid>
            <Grid item>
              <Typography className={classes.sectionDescription}>
                {description}
              </Typography>
            </Grid>
          </Grid>
          <Divider />
          <Grid item>
            {(settings ?? []).map((x: ConfigurationSetting) => (
              <List dense={true} className={classes.sectionSettings}>
                <ListItem>
                  <ListItemText
                    primary={`${x.name}`}
                    secondary={`${x.value}`}
                  />
                </ListItem>
              </List>
            ))}
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Button
          className={classes.sectionAction}
          color="primary"
          onClick={onEditClicked}
        >
          {editText}
        </Button>
        {children && children(classes.sectionAction)}
      </CardActions>
    </Card>
  )
}
